import moment from 'moment';
import { catchError, defer, from, map, Observable, of } from 'rxjs';

// Added because msSaveBlob wasn't recognized by the TS Compiler when testing
declare global {
  interface Navigator {
    msSaveBlob?: (blob: Blob, defaultName?: string) => boolean;
  }
}

export class FileDownload {
  static downloadFile(fileName: string, encodedData: string) {
    const downloadTag = document.createElement('a');

    downloadTag.href = encodedData;
    downloadTag.download = fileName;

    // if (ApplicationConfig.onIosMobile()) {
    //   downloadTag.target = '_blank';
    // }

    document.body.appendChild(downloadTag);
    downloadTag.click();
    document.body.removeChild(downloadTag);
  }

  static formatCsvCell(cellValue: string | number): string {
    if (cellValue !== 0 && (!cellValue || cellValue === 'null')) {
      return 'Not Available';
    } else {
      return `"${cellValue.toString().replace(/"/g, '""')}"`;
    }
  }

  /**
   * Adds generic information to a csv and exports the file.
   * @param filename The name if the file to be exported.
   * @param data The body of the file as a comma-delimited string
   * @param [attributionUrl] Optional: adds the attribution url to the bottom of the file.
   * @param [generateCreationTime] Optional: if true, adds the date the report was created. if false, nothing is added. defaults to true.
   */
  static downloadCSV(filename: string, data: string, attributionUrl?: string, generateCreationTime: boolean = true) {
    // const env = (window as any).eaglei.env;
    const exportFilename = `${filename}_${moment().format('YYYYMMDDhhmmss')}.csv`;

    // if (env !== 'production') {
    //   data = `Data is from ${env} environment.\n\n` + data;
    //   exportFilename = `${env}_${exportFilename}`;
    // }

    if (attributionUrl) {
      const attr = attributionUrl.split(',').join('\n');

      data += `\n\n Attribution URL: ${attr}`;
    }

    if (generateCreationTime) {
      data += `\n\n Report Created At ${moment().format('M/D/YYYY hh:mm A')}`;
    }

    const blob = new Blob([data], { type: 'text/csv' });

    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, exportFilename);
    } else {
      const url = window.URL.createObjectURL(blob);
      FileDownload.downloadFile(exportFilename, url);
    }
  }

  /**
   * Copies the provided text to the user's clipboard.
   * Uses the modern Clipboard API when available and falls back to a hidden
   * textarea with `document.execCommand("copy")` for older browser support.
   *
   * @param textToCopy The text value to copy to the clipboard.
   * @returns An observable that emits `true` when the copy succeeds and `false`
   * if the text is empty or the copy operation fails.
   */
  static copyTextToClipBoard(textToCopy: string): Observable<boolean> {
    if (!textToCopy) {
      return of(false);
    }

    const fallbackCopy = () => {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = textToCopy;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();

        const copied = document.execCommand('copy');
        document.body.removeChild(textarea);
        return copied;
      } catch {
        return false;
      }
    };


    return defer(() => {
      if (navigator.clipboard?.writeText) {
        return from(navigator.clipboard.writeText(textToCopy)).pipe(
          map(() => true),
          catchError(() => of(fallbackCopy())),
        );
      }

      return of(fallbackCopy());
    }).pipe(
      catchError(() => of(false)),
    );
  }

}
